import { Fragment } from 'react'
import { addDays, dow, fmt, holidayOn, placeName, placeOf, type Period } from '../lib/calendar'
import { secondHomeCountry, secondHomeOf, useProfile } from '../lib/profile'
import { homeName, useRegionsNow } from '../lib/regions'
import { useRegionHolidays } from '../lib/currentRegion'
import { ribbonCells, type RibbonHoliday } from '../lib/ribbon'
import type { YearItem } from '../lib/yearItems'
import type { Visitor } from '../lib/types'
import { PeriodCard } from './PeriodCard'
import { VisitorCard } from './VisitorCard'

const monthEnd = (ym: string) => {
  const [y, m] = ym.split('-').map(Number)
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
}
const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

type StackEntry =
  | { on: string; kind: 'card'; period: Period; itemsFrom: string; itemsTo: string }
  | { on: string; kind: 'continues'; period: Period }
  | { on: string; kind: 'visitor'; visitor: Visitor }

/**
 * One period's place in this month's stack.
 *
 * A trip (the second home or Travel) gets exactly one full `PeriodCard`, ever — in
 * the month containing its true start, or, when it was already under way
 * before the whole visible window opened (`lo`), in the month containing
 * `lo` instead but only if it is the one Doug is in right now. Every other
 * month it touches gets a one-line "continues until" note (rule 3's third
 * shape).
 *
 * Berlin is different, on purpose: it is the absence of a stay, so its own
 * card carries no fixed identity to keep consistent across months the way a
 * named trip's does. Re-deriving `periods()` per month used to fragment a
 * long *quiet* Berlin stretch into "BERLIN, BERLIN, BERLIN" down the page
 * with nothing in any of them (`calendar.ts`'s own header) — the fix for
 * that bug is not "never show it twice", it is "never show it with nothing
 * to say". So a Berlin period gets its own card in *every* month it
 * overlaps, but only where that month actually has an item on it (or it is
 * the month Doug is in right now), and each such card's items are clipped to
 * that one month — the first draft of this file instead attached *every*
 * item across the period's entire span to whichever month it started in,
 * which meant a Berlin stretch running from October into next year dumped
 * fifteen months of birthdays into a single October card.
 */
function stackFor(
  monthFirst: string, monthLast: string, lo: string, today: string,
  periods: Period[], visitors: Visitor[], items: YearItem[],
): StackEntry[] {
  const entries: StackEntry[] = []
  for (const p of periods) {
    if (p.end < monthFirst || p.start > monthLast) continue
    const isNow = today >= p.start && today <= p.end

    if (p.place === 'Home') {
      const clipFrom = p.start > monthFirst ? p.start : monthFirst
      const clipTo = p.end < monthLast ? p.end : monthLast
      const monthItems = items.some(i => i.on >= clipFrom && i.on <= clipTo)
      const isNowHere = isNow && today >= monthFirst && today <= monthLast
      if (monthItems || isNowHere) {
        const on = clipFrom > lo ? clipFrom : lo
        entries.push({ on, kind: 'card', period: { ...p, start: clipFrom, end: clipTo }, itemsFrom: clipFrom, itemsTo: clipTo })
      }
      continue
    }

    const effectiveOn = p.start >= lo ? p.start : lo
    const inThisMonth = effectiveOn >= monthFirst && effectiveOn <= monthLast
    if (p.start >= lo) {
      if (inThisMonth) entries.push({ on: p.start, kind: 'card', period: p, itemsFrom: p.start, itemsTo: p.end })
    } else if (isNow) {
      if (inThisMonth) entries.push({ on: lo, kind: 'card', period: p, itemsFrom: p.start, itemsTo: p.end })
    } else {
      entries.push({ on: monthFirst > lo ? monthFirst : lo, kind: 'continues', period: p })
    }
  }
  for (const v of visitors) {
    if (v.starts_on < monthFirst || v.starts_on > monthLast) continue
    entries.push({ on: v.starts_on, kind: 'visitor', visitor: v })
  }
  // A period's card sorts before a visitor arriving the same day — matching
  // the prototype's own tie-break.
  return entries.sort((a, b) => (a.on !== b.on ? (a.on < b.on ? -1 : 1) : a.kind === 'visitor' ? 1 : -1))
}

export function MonthRibbon({
  ym, lo, today, periods, visitors, items,
  onDayTap, onEditPeriod, onEditItem, onRemoveItem, onEditVisitor,
}: {
  ym: string
  /** The whole visible window's earliest day — today (WP 8.5: the Year is
   *  forward-looking, not this week's Monday). Passed unchanged to every
   *  month; only the current (partial) one ever has `lo > ` its own 1st, so
   *  every later month's `start` resolves to its own 1st on its own. Nothing
   *  here assumes `lo` falls on any particular weekday — each cell's own
   *  `dow(c.on)` picks its weekday letter directly (`ribbonCells`), so this
   *  is not a Monday-aligned weeks grid to begin with. */
  lo: string
  today: string
  /** The whole displayed span's periods, true bounds, computed once by the
   *  caller — never re-derived per month (see `stackFor`'s own header). */
  periods: Period[]
  visitors: Visitor[]
  /** Commitments, flights, birthdays, tasks and tentative saved events —
   *  everything `yearItems()` produces, for the whole displayed span. */
  items: YearItem[]
  onDayTap: (day: string) => void
  onEditPeriod: (p: Period) => void
  onEditItem: (item: YearItem) => void
  onRemoveItem: (item: YearItem) => void
  onEditVisitor: (v: Visitor) => void
}) {
  const first = `${ym}-01`
  const last = monthEnd(ym)
  const start = first > lo ? first : lo
  // U1.3: what a second-home period is called, flagged and kept holidays by
  // is the profile's. Read once for the month and handed to its cards.
  const profile = useProfile()
  const second = secondHomeOf(profile)
  const secondCountry = secondHomeCountry(profile)
  // C2.1: what a `'Home'` period is called — the home region's name.
  const home = homeName(useRegionsNow(), profile)
  const holidayKey = useRegionHolidays()

  const holidays: RibbonHoliday[] = []
  for (let d = start; d <= last; d = addDays(d, 1)) {
    const place = placeOf(periods, d)?.place ?? 'Home'
    const name = holidayOn(d, place === 'Home2' ? 'Home2' : 'Home', secondCountry, holidayKey)
    if (name) holidays.push({ on: d, name })
  }

  const cells = ribbonCells(ym, {
    lo, today, periods, holidays,
    visitors: visitors.map(v => ({ starts_on: v.starts_on, ends_on: v.ends_on, at_mine: v.at_mine })),
  })

  const birthdaysCount = items.filter(i => i.kind === 'birthday' && i.on >= start && i.on <= last).length
  const visitorsCount = visitors.filter(v => v.starts_on <= last && v.ends_on >= first).length
  const bits = [
    birthdaysCount ? `${birthdaysCount} birthday${birthdaysCount === 1 ? '' : 's'}` : null,
    holidays.length ? `${holidays.length} holiday${holidays.length === 1 ? '' : 's'}` : null,
    visitorsCount ? `${visitorsCount} visitor${visitorsCount === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ')

  const stack = stackFor(first, last, lo, today, periods, visitors, items)

  return (
    <section className="month">
      <div className="mh">
        <h3>{fmt(first, { month: 'long', year: 'numeric' })}</h3>
        <span>{bits || 'nothing yet'}</span>
      </div>

      {/* `grid-template-columns` is set here, not in `app.css`: a 28- or
          30-day month has to fill its own row edge to edge rather than a
          fixed 31-wide grid leaving a gap only some months have. */}
      <div className="dowrow" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0,1fr))` }}>
        {cells.map(c => <span key={c.on} className={c.weekend ? 'we' : ''}>{WEEKDAY_LETTERS[dow(c.on)]}</span>)}
      </div>
      <div className="ribbon" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0,1fr))` }}>
        {/* A tap opens the day, so each day stays a button — but the button
            is the hit area (`.rch`: the whole column, gap included, 44px
            tall) and the pill drawn inside it is a span. The pill's own
            ::before and ::after are the holiday dot and the visitor stripe,
            so a pseudo-element could not be the hit area (UI-audit a4-11). */}
        {cells.map(c => (
          <button
            key={c.on}
            type="button"
            className="rch"
            aria-label={`${fmt(c.on, { weekday: 'long', day: 'numeric', month: 'long' })}${c.holiday ? `, ${c.holiday}` : ''}`}
            onClick={() => onDayTap(c.on)}
          >
            <span
              aria-hidden="true"
              className={[
                'rc', c.away ? 'Away' : '', c.weekend ? 'we' : '', c.holiday ? 'hol' : '',
                c.visitorStripe ? 'vis' : '', c.today ? 'today' : '', c.out ? 'out' : '',
              ].filter(Boolean).join(' ')}
            />
          </button>
        ))}
      </div>
      <div className="rnum" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0,1fr))` }}>
        {cells.map(c => {
          const dn = Number(c.on.slice(8))
          return <span key={c.on}>{dn === 1 || dn % 5 === 0 ? dn : ''}</span>
        })}
      </div>

      {holidays.length > 0 && (
        <div className="holcap">
          {holidays.map(h => (
            <span key={h.on}>
              <i aria-hidden="true" /><b>{fmt(h.on, { weekday: 'short', day: 'numeric' })}</b> · {h.name}
              {placeOf(periods, h.on)?.place === 'Home2' ? ` (${placeName({ place: 'Home2', label: null }, second, home)})` : ''}
            </span>
          ))}
        </div>
      )}

      <div className="stack">
        {stack.map(entry => {
          if (entry.kind === 'visitor') {
            return (
              <VisitorCard
                key={`v${entry.visitor.id}`} visitor={entry.visitor} periods={periods} second={second}
                onEdit={() => onEditVisitor(entry.visitor)}
              />
            )
          }
          if (entry.kind === 'continues') {
            const p = entry.period
            return (
              <p className="note" key={`c${p.start}`}>
                {placeName(p, second, home)} continues until{' '}
                {fmt(p.end, { day: 'numeric', month: 'short' })}
              </p>
            )
          }
          const p = entry.period
          const periodItems = items.filter(i => i.on >= entry.itemsFrom && i.on <= entry.itemsTo)
          return (
            <Fragment key={`p${p.start}`}>
              <PeriodCard
                p={p} today={today} items={periodItems} second={second} secondCountry={secondCountry}
                onEditPeriod={p.stayId ? () => onEditPeriod(p) : undefined}
                onEditItem={onEditItem} onRemoveItem={onRemoveItem}
              />
            </Fragment>
          )
        })}
      </div>
    </section>
  )
}

